import { processNumericValue } from "@src/core/engine/helpers/processNumericValue";
import {
  isInlineValueUnit,
  PhysicalType,
} from "@src/core/engine/units/constants";
import {
  ValidationConstraints,
  ValidationResult,
} from "@src/core/engine/validationResults/buildValidationResult";
import { UnitConversionFn } from "@src/shared/hooks/useUnitConversion";
import { TFunction } from "i18next";
import { isNumber } from "radashi";
import { isNotNil } from "../../../shared/helpers/guards";

export type TranslationHandler = {
  prefix?: string;
  t: TFunction;
};

type ParseValidationErrorOptions = {
  constraintsT: TranslationHandler;
  conversionFn?: UnitConversionFn;
  fieldsT: TranslationHandler;
  getSymbol?: (physicalType: PhysicalType | null | undefined) => string | null;
  maxDecimals?:
    | {
        max?: number;
        min?: number;
        value?: number;
      }
    | number;
  validationResult: ValidationResult;
};

export function parseValidationResult(
  locales: string,
  {
    constraintsT,
    conversionFn,
    fieldsT,
    getSymbol,
    maxDecimals = 3,
    validationResult,
  }: ParseValidationErrorOptions,
) {
  const { code, constraints, path } = validationResult;
  const unit = getUnit(constraints, getSymbol);
  const constraintsPrefix = constraintsT.prefix ?? "validation";
  const fieldsPrefix = fieldsT.prefix ?? "validation.fields";

  if (code === "custom") {
    return fieldsPrefix === ""
      ? fieldsT.t(`${validationResult.translationKey}`)
      : fieldsT.t(`${fieldsPrefix}.${validationResult.translationKey}`);
  }

  const constraintKey = [
    constraintsPrefix === "" ? code : `${constraintsPrefix}.${code}`,
    constraints.sizeMax || constraints.sizeMin ? "size" : "",
    constraints.reference ? "referenced" : "default",
  ]
    .filter(Boolean)
    .join(".");
  const formatOptions = {
    physicalType: constraints.physicalType,
  };
  const numberValueArgs = { conversionFn, round: true };
  const fieldKey = fieldsPrefix === "" ? path : `${fieldsPrefix}.${path}`;

  return constraintsT.t(constraintKey, {
    field: fieldsT.t(fieldKey, {
      interpolation: { escapeValue: false },
    }),
    interpolation: { escapeValue: false },
    max: formatValue(
      locales,
      unit,
      constraints.max,
      {
        ...formatOptions,
        maxDecimal: isNumber(maxDecimals) ? maxDecimals : maxDecimals?.max,
      },
      { ...numberValueArgs, round: "floor" },
    ),
    min: formatValue(
      locales,
      unit,
      constraints.min,
      {
        ...formatOptions,
        maxDecimal: isNumber(maxDecimals) ? maxDecimals : maxDecimals?.min,
      },
      { ...numberValueArgs, round: "ceil" },
    ),
    reference: constraints.reference
      ? fieldsT.t(`${fieldsPrefix}.${constraints.reference}`).toLowerCase()
      : null,
    sizeMax: constraints.sizeMax,
    sizeMin: constraints.sizeMin,
    value: formatValue(
      locales,
      unit,
      constraints.value,
      {
        ...formatOptions,
        maxDecimal: isNumber(maxDecimals) ? maxDecimals : maxDecimals?.value,
      },
      { conversionFn, round: true },
    ),
  });
}

function formatValue(
  locales: string,
  unit: string,
  ...args: Parameters<typeof processNumericValue>
) {
  const [value, ...options] = args;
  if (!isNotNil(value) || !isNumber(value)) return null;
  const processedValue = processNumericValue(value, ...options);
  return `${Intl.NumberFormat(locales, {
    maximumFractionDigits: 100,
  }).format(processedValue)}${unit}`;
}

const getUnit = (
  constraints: ValidationConstraints,
  getSymbol?: (physicalType: PhysicalType | null | undefined) => string | null,
) =>
  isInlineValueUnit(constraints?.physicalType) && getSymbol
    ? ` ${getSymbol(constraints.physicalType)}`
    : "";
